import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Key, X as XIcon, EnvelopeSimple, ChatCircleText, Copy, Check, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminResetPasswordDialog = ({ user, open, onClose }) => {
  const [channel, setChannel] = useState('email');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { temp_password, channel, delivered, email }
  const [copied, setCopied] = useState(false);

  if (!open || !user) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/users/${user.user_id}/reset-password`,
        { channel }
      );
      setResult(data);
      toast.success('Temporary password generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally { setBusy(false); }
  };

  const copyPwd = async () => {
    if (!result?.temp_password) return;
    try {
      await navigator.clipboard.writeText(result.temp_password);
      setCopied(true);
      toast.success('Password copied');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { toast.error('Copy failed'); }
  };

  const close = () => {
    setResult(null);
    setChannel('email');
    setCopied(false);
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-reset-password-dialog">
      <div className="bg-[#161b26] border border-[#252d3d] rounded-2xl p-5 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Key size={18} weight="fill" className="text-amber-400" />
            <h4 className="text-white text-base font-semibold">Regenerate Password</h4>
          </div>
          <button onClick={close} className="text-[#6b7a99] hover:text-white"><XIcon size={18} /></button>
        </div>

        <div className="bg-[#0f1320] border border-[#252d3d] rounded-lg p-3 mb-4">
          <p className="text-[10px] text-[#6b7a99] uppercase tracking-wider mb-1">For user</p>
          <p className="text-sm text-white font-semibold">{user.name || '—'}</p>
          <p className="text-xs text-[#a5b4d0]">{user.email}</p>
          <p className="text-[10px] text-[#6b7a99] mt-1">role: <span className="text-[#8896b0] font-mono">{user.role}</span></p>
        </div>

        {!result ? (
          <>
            <div className="mb-4">
              <label className="text-[10px] text-[#6b7a99] uppercase tracking-wider">Delivery channel</label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger className="h-9 text-sm mt-1 bg-[#0f1320] border-[#252d3d] text-white" data-testid="reset-channel-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">📧 Email — send to {user.email}</SelectItem>
                  <SelectItem value="sms">💬 SMS — send to user&apos;s phone</SelectItem>
                  <SelectItem value="none">🔒 Manual — show me, I&apos;ll share it</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[#6b7a99] mt-2 leading-snug flex items-start gap-1.5">
                <Warning size={12} className="text-amber-400 shrink-0 mt-0.5" />
                Email/SMS delivery is currently <strong className="text-amber-300">MOCKED</strong> (logged to backend console). The temp password is always returned here so you can share it directly. User will be forced to change it on next login.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={close} className="text-[#8896b0] hover:text-white h-9 rounded-lg text-xs">Cancel</Button>
              <Button size="sm" onClick={submit} disabled={busy}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 h-9 rounded-lg text-xs font-semibold" data-testid="reset-confirm-btn">
                <Key size={14} className="mr-1" weight="fill" /> {busy ? 'Generating…' : 'Generate Password'}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent border border-emerald-400/30 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-1.5 text-emerald-300 text-xs font-semibold mb-2">
                <Check size={14} weight="bold" /> Password regenerated
              </div>
              <p className="text-[10px] text-[#6b7a99] uppercase tracking-wider mb-1">Temporary password</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-[#0f1320] border border-[#252d3d] rounded-md px-3 py-2 text-emerald-300 font-mono text-sm tracking-wider" data-testid="temp-password-display">
                  {result.temp_password}
                </code>
                <button onClick={copyPwd} className="p-2 rounded-md bg-[#0f1320] border border-[#252d3d] text-[#8896b0] hover:text-white" title="Copy" data-testid="copy-temp-password-btn">
                  {copied ? <Check size={14} weight="bold" className="text-emerald-400" /> : <Copy size={14} />}
                </button>
              </div>
              <div className="mt-3 flex items-start gap-1.5 text-[11px] text-[#a5b4d0]">
                {result.channel === 'email' && <EnvelopeSimple size={12} className="shrink-0 mt-0.5 text-indigo-300" />}
                {result.channel === 'sms' && <ChatCircleText size={12} className="shrink-0 mt-0.5 text-indigo-300" />}
                <p>
                  {result.channel === 'email' && (result.delivered
                    ? <>Mock email sent to <span className="text-white">{result.email}</span> (logged to backend console).</>
                    : <>Email delivery failed — recipient has no email on file. Share the password manually.</>)}
                  {result.channel === 'sms' && (result.delivered
                    ? <>Mock SMS sent to user&apos;s phone (logged to backend console).</>
                    : <>SMS delivery failed — recipient has no phone on file. Share manually.</>)}
                  {result.channel === 'none' && <>Share this password with the user via your preferred channel.</>}
                </p>
              </div>
              <p className="text-[10px] text-amber-300 mt-2 leading-snug">
                The user MUST change this password on next login. The new password expires automatically when they set their own.
              </p>
            </div>
            <div className="flex justify-end">
              <Button size="sm" onClick={close} className="bg-slate-700 hover:bg-slate-600 text-white h-9 rounded-lg text-xs" data-testid="reset-done-btn">Done</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminResetPasswordDialog;
